import type { AppState, ClassificationScenario, VectorLayerModel } from '../core/types';
import { masterClassificationAssignments, type ClassificationAssignments } from '../domain/classification';
import { deriveDisplaySet } from '../domain/displaySet';
import { activePartialExceptionField, mixedClassificationAssignments, type MixedThematicPiece } from '../domain/mixedThematic';
import { scenarioPartialAssignments } from '../domain/scenarioPartials';
import { scenarioClassificationAssignments, scenarioStatus, shownScenario } from '../domain/scenarios';
import { visibleThemeTargetIds } from '../domain/themeTarget';

export interface MasterTheme {
  readonly assignments: ClassificationAssignments;
  /** Classified Maestro fragments: always the visible theme target (an Área parcial never widens it). */
  readonly subsetIds: ReadonlySet<string>;
  /** Pieces per entity: Áreas parciales (`mixed`, by Suerte) or a scenario (by entity of the current «Ver por»). */
  readonly piecesBySuerteId: ReadonlyMap<string, readonly MixedThematicPiece[]>;
  readonly mixed: boolean;
  /** Áreas parciales in effect (as in «Actual»), by Suerte: also while a scenario is drawn over them. */
  readonly partialPieces?: ReadonlyMap<string, readonly MixedThematicPiece[]>;
  /** V1.9.0: the scenario shown (Propuesta); absent = «Actual». */
  readonly scenario?: ClassificationScenario;
}

const NO_PIECES: ReadonlyMap<string, readonly MixedThematicPiece[]> = new Map();

/**
 * Single entry point for the Maestro classification shown on screen, in the legend and in every export:
 * the normal per-entity assignments, the Suerte + Áreas parciales composition when exceptions are on, and a shown
 * scenario laid over either of them (V1.9.0; precedence in domain/scenarioPartials).
 */
export function masterThemeAssignments(state: AppState, layer: VectorLayerModel): MasterTheme | null {
  const config = state.classificationConfig;
  if (!state.model || !config.field || layer.kind !== 'master') return null;
  const targetIds = visibleThemeTargetIds(state, layer);
  const shown = shownScenario(state.scenarios);
  const scenario = shown && scenarioStatus(shown, config).applies ? shown : null;
  const partialField = activePartialExceptionField(config, state.model, state.entityViewLevel, state.operationalFields);
  if (partialField) {
    const mixed = mixedClassificationAssignments({
      model: state.model, baseField: config.field, partialField, targetIds,
      displayIds: deriveDisplaySet(state).masterFragmentIds, subareas: state.subareas, fields: state.operationalFields, split: config,
    });
    if (!scenario) return { assignments: mixed, subsetIds: mixed.subsetFragmentIds, piecesBySuerteId: mixed.piecesBySuerteId, mixed: true, partialPieces: mixed.piecesBySuerteId };
    const assignments = scenarioPartialAssignments(state.model, mixed, scenario);
    return { assignments, subsetIds: mixed.subsetFragmentIds, piecesBySuerteId: assignments.piecesByEntityId, mixed: true, partialPieces: mixed.piecesBySuerteId, scenario };
  }
  if (scenario) {
    const assignments = scenarioClassificationAssignments(state.model, config, state.entityViewLevel, targetIds, state.operationalFields, scenario);
    return { assignments, subsetIds: targetIds, piecesBySuerteId: assignments.piecesByEntityId, mixed: false, scenario };
  }
  const assignments = masterClassificationAssignments(state.model, config.field, state.entityViewLevel, targetIds, state.operationalFields, { ...config, level: state.entityViewLevel });
  return { assignments, subsetIds: targetIds, piecesBySuerteId: NO_PIECES, mixed: false };
}

const exceptionIdsCache = new WeakMap<ReadonlyMap<string, readonly MixedThematicPiece[]>, ReadonlySet<string>>();
const NO_IDS: ReadonlySet<string> = new Set();

/**
 * Subareas that currently act as exceptions (their Suerte is visible AND in the ThemeTarget): their own layer draws
 * them as outlines so the composition reads as one map. Any other subarea keeps the layer's normal style.
 */
export function exceptionSubareaIds(state: AppState): ReadonlySet<string> {
  // Cheap guard first: without the mixed view nothing is classified here (no extra work in normal mode).
  if (!activePartialExceptionField(state.classificationConfig, state.model, state.entityViewLevel, state.operationalFields)) return NO_IDS;
  const layer = state.layers.find(({ id }) => id === state.model?.id);
  const theme = layer ? masterThemeAssignments(state, layer) : null;
  // The exceptions of «Actual», also under a scenario: a subarea covered by a proposal stays an outline, not a fill.
  const pieces = theme?.partialPieces; if (!pieces) return NO_IDS;
  const cached = exceptionIdsCache.get(pieces); if (cached) return cached;
  const ids = new Set([...pieces.values()].flatMap((items) => items.filter(({ source }) => source === 'subarea').map(({ sourceId }) => sourceId)));
  exceptionIdsCache.set(pieces, ids);
  return ids;
}
